// Copyright (c) 2022 by Move Closer

import { computed, PropType, ref, watch, toRefs, ComponentInternalInstance, onMounted } from '@vue/composition-api'

import { ComponentObjectPropsOptions, Emit } from '../../../vue-api'

import { AccordionItemProps } from './AccordionItem.contracts'

import {
  AccordionContent
} from '../Accordion.contracts'

/**
 * @author Jan Dobrowolski <jan.dobrowolski@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Katarzyna Otto <katarzyna.otto@movecloser.pl>
 */
export const accordionItemProps: ComponentObjectPropsOptions<AccordionItemProps> = {
  /**
   * Determines whether the item should be opened.
   */
  isOpen: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * Item's label.
   */
  label: {
    type: [String, Function, Object] as PropType<string | AccordionContent>,
    required: false,
    default: ''
  },

  /**
   * Properties of label component
   */
  labelProps: {
    type: Object,
    required: false,
    default: () => ({})
  }
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const useAccordionItem = (props: AccordionItemProps, emit: Emit, internalInstance: ComponentInternalInstance | null) => {
  if (internalInstance === null) {
    throw new Error('useLink(): FATAL ERROR! Failed to resolve the component instance!')
  }

  // Extract the `type` prop.
  const { isOpen, type } = toRefs(props)
  const isVisible = ref(false)
  const maxHeight = ref(0)

  const isSimpleLabel = computed<boolean>(() => {
    return typeof props.label === 'string'
  })

  const Component = computed<any>(() => {
    return typeof props.label === 'string' ? 'span' : props.label.component
  })

  const setMaxHeight = () => {
    const contentRef = internalInstance.refs.contentRef as HTMLDivElement

    if (contentRef) {
      contentRef.style.maxHeight = ''
      maxHeight.value = contentRef.scrollHeight
    }
  }

  /**
   * Handles the @click event on the root HTML element.
   */
  const onClick = () => {
    emit('click')
  }

  watch(isOpen, (isOpen) => {
    setTimeout(() => {
      isVisible.value = isOpen
    }, 500)
  })

  onMounted(() => {
    window.addEventListener('resize', setMaxHeight)
    setTimeout(() => {
      setMaxHeight()
    }, 500)
  })

  return { Component, isSimpleLabel, maxHeight, onClick, isVisible }
}
